import { useState } from "react";
import type { EventConflict } from "../../api/types";
import { formatDateTime } from "../../lib/format";
import { Card, Field, Notice } from "../../ui/components";

export interface ConflictResult {
  conflicts: EventConflict[];
  canOverride: boolean;
}

// The refusal names what is in the way and who owns it. "The hall is taken" is not something
// anybody can act on: whether the thing standing there is a hire somebody has paid for or one of
// the client's own events decides who gets asked to move.
function ownerLabel(conflict: EventConflict): string {
  if (conflict.ownerKind === "booking") return "Booked by a customer";
  if (conflict.ownerKind === "event") return "One of our own events";
  return "Held while somebody books online";
}

export function EventConflictPanel({
  result,
  submitting,
  onCancel,
  onOverride,
}: {
  result: ConflictResult;
  submitting: boolean;
  onCancel(): void;
  onOverride(reason: string): void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");
  const ready = acknowledged && reason.trim().length >= 10;

  return (
    <Card>
      <div className="stack">
        <Notice tone="danger" title="This time is already taken">
          Every reservation below overlaps the hours this event would hold. Read them before
          deciding what to do.
        </Notice>
        {result.conflicts.map((conflict) => (
          <div className="card" key={conflict.allocationId}>
            <strong>
              {conflict.reference ? `${conflict.reference} · ` : ""}
              {conflict.title}
            </strong>
            <p>
              {ownerLabel(conflict)}
              <br />
              {conflict.locationName}
              <br />
              {formatDateTime(conflict.start)} to {formatDateTime(conflict.end)}
            </p>
          </div>
        ))}
        {result.canOverride ? (
          <>
            <label className="check-field">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>
                I understand this deliberately double-books the room and the decision is recorded
                against my name.
              </span>
            </label>
            <Field
              label="Required override reason"
              hint="At least 10 characters, specific enough to make sense to somebody reading it a year from now."
            >
              <textarea
                value={reason}
                minLength={10}
                maxLength={1000}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            <div className="form-actions">
              <button className="button secondary" type="button" onClick={onCancel}>
                Change the schedule
              </button>
              <button
                className="button danger"
                type="button"
                disabled={submitting || !ready}
                onClick={() => onOverride(reason.trim())}
              >
                Confirm override
              </button>
            </div>
          </>
        ) : (
          <>
            <Notice tone="warning">
              Your role cannot double-book a room. Change the schedule, or ask a colleague who can.
            </Notice>
            <button className="button secondary" type="button" onClick={onCancel}>
              Change the schedule
            </button>
          </>
        )}
      </div>
    </Card>
  );
}
